import { useEffect, useState, useCallback } from 'react';
import { useChat } from '../contexts/ChatContext';
import { useChatPopup } from '../contexts/ChatPopupContext';
import { useAuth } from '../contexts/AuthContext';

interface PopupNotification {
  id: string;
  message: string;
  senderName: string;
  timestamp: Date;
  type: 'message' | 'mention';
}

/**
 * Hook para gerenciar notificações dentro do popup do chat
 * Detecta novas mensagens quando o popup está aberto mas talvez fora de foco
 */
export const usePopupNotifications = () => {
  const { user } = useAuth();
  const { messages, currentRoom } = useChat();
  const { isOpen, isMinimized } = useChatPopup();
  const [notifications, setNotifications] = useState<PopupNotification[]>([]);
  const [lastMessageId, setLastMessageId] = useState<string | null>(null);
  const [isWindowFocused, setIsWindowFocused] = useState(true);
  const [hasNewMessages, setHasNewMessages] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);

  // Função para tocar som de notificação
  const playNotificationSound = useCallback((type: 'message' | 'mention') => {
    if (!soundEnabled) return;
    
    try {
      // Criar tom de notificação com Web Audio API
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Frequências diferentes para tipos diferentes
      oscillator.frequency.setValueAtTime(type === 'mention' ? 800 : 600, audioContext.currentTime);
      oscillator.type = 'sine';
      
      // Volume baixo para não ser intrusivo
      gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (error) {
      console.warn('Não foi possível tocar som de notificação:', error);
    }
  }, [soundEnabled]);

  // Detectar se a janela está em foco
  useEffect(() => {
    const handleFocus = () => setIsWindowFocused(true);
    const handleBlur = () => setIsWindowFocused(false);

    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);

  // Detectar novas mensagens quando popup está aberto
  useEffect(() => {
    if (!user || !currentRoom || !isOpen || isMinimized || messages.length === 0) {
      return;
    }

    const latestMessage = messages[messages.length - 1];
    
    // Se é uma nova mensagem (diferente da última processada)
    if (lastMessageId !== latestMessage.id) {
      setLastMessageId(latestMessage.id);
      
      // Se a mensagem não é do usuário atual
      if (latestMessage.user_auth_id !== user.id) {
        // Marcar que há novas mensagens
        setHasNewMessages(true);
        
        // Se a janela não está em foco, criar notificação
        if (!isWindowFocused) {
          const senderName = latestMessage.user?.name || 'Usuário';
          const ismentionado = latestMessage.content.includes(`@${user.email?.split('@')[0]}`) ||
                              latestMessage.mentions?.some(mention => mention.user_auth_id === user.id);
          
          const notification: PopupNotification = {
            id: latestMessage.id,
            message: latestMessage.content,
            senderName,
            timestamp: new Date(latestMessage.created_at),
            type: ismentionado ? 'mention' : 'message'
          };

          setNotifications(prev => [...prev.slice(-4), notification]); // Manter apenas 5 notificações
          
          // Tocar som de notificação
          playNotificationSound(notification.type);
        }
      }
    }
    // Depende de `user?.id`, não do objeto `user` (ver App.tsx) — evita reprocessar
    // toda vez que a aba volta a ficar visível.
  }, [messages, user?.id, currentRoom, isOpen, isMinimized, lastMessageId, isWindowFocused]);

  // Limpar indicador de novas mensagens quando janela ganha foco
  useEffect(() => {
    if (isWindowFocused && isOpen && !isMinimized) {
      setHasNewMessages(false);
    }
  }, [isWindowFocused, isOpen, isMinimized]);

  // Limpar notificações quando popup é fechado ou minimizado
  useEffect(() => {
    if (!isOpen || isMinimized) {
      setNotifications([]);
      setHasNewMessages(false);
    }
  }, [isOpen, isMinimized]);

  const dismissNotification = useCallback((notificationId: string) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
  }, []);

  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
    setHasNewMessages(false);
  }, []);

  return {
    notifications,
    hasNewMessages,
    isWindowFocused,
    soundEnabled,
    setSoundEnabled,
    dismissNotification,
    clearAllNotifications
  };
};